import { ButtonPrimary, ButtonTertiary } from "components/Atoms/Button";
import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader, PreviewPanelHorizontalDivider } from "../PreviewPanel";
import AppointmentPhase from "components/Atoms/AppointmentPhase";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import { PreviewPanelDepartureArrival } from "components/Atoms/details/PreviewPanelDepartureArrival";
import { JourneySegment } from "components/Atoms/JourneySegment";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { AirportPreview } from "../Airport/AirportPreviewPanel";
import { ArtistPreview } from "../Company/CompanyPreviewPanel";
import { PreviewPanelContactList } from "../contact/PreviewPanelContactList";

interface FlightSegmentPreviewPanelProps {
  flightSegmentId: number
}

const FlightSegmentPreviewPanel = (props: FlightSegmentPreviewPanelProps) => {
  const { flightSegmentId } = props

  const api = useApi(() => window.Api.Flights.GetFlightBySegment(flightSegmentId), ["flight-segment", flightSegmentId], {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [flightSegmentId]);

  if (api.isInitialLoading) {
    return <>
      <PreviewPanelHeader title={RESX.Flight.resxFlight} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  const flight = api.data;
  const flightSegment = api.data.segments.find(segment => segment.id === flightSegmentId);

  const startDateWritten = formatWrittenDayMonthYear(flightSegment.departure.dateTimeLocal);
  const endDateWritten = formatWrittenDayMonthYear(flightSegment.departure.dateTimeLocal);

  const startTimeWritten = formatTime(flightSegment.departure.dateTimeLocal);
  const endTimeWritten = formatTime(flightSegment.arrival.dateTimeLocal);

  return <>
    <PreviewPanelHeader title={RESX.Flight.resxFlight} />
    <PreviewPanelContent>
      {flight.artist && <PreviewPanelDetailRow labelTop={site.currentEnvironment.functionGroups.artist.singular}>
        <ArtistPreview
          companyId={flight.artist.artistId}
          trigger={<a>{flight.artist.name}</a>}
          fallbackIfNoPermission={<>{flight.artist.name}</>}
        />
      </PreviewPanelDetailRow>}

      {flight.travellers.length > 0 && <PreviewPanelDetailRow labelTop={RESX.Flight.Travellers}>
        <PreviewPanelContactList contacts={flight.travellers} />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
        <AppointmentPhase value={flight.phase} displayLabel={true} />
      </PreviewPanelDetailRow>

      <PreviewPanelHorizontalDivider />

      <PreviewPanelDepartureArrival
        dateWritten={startDateWritten}
        timeWritten={startTimeWritten}
        sideInfo={flightSegment.departure.airport.iata || flightSegment.departure.airport.icao}
        addressLine1={<AirportPreview airportId={flightSegment.departure.airport.airportId} trigger={<a onClick={() => { }}>{flightSegment.departure.airport.name}</a>} />}
        addressLine2={`${flightSegment.departure.airport.location}`}
      />

      <JourneySegment
        duration={flightSegment.duration}
        distance={flightSegment.distanceInMeters}
      />

      <PreviewPanelDepartureArrival
        dateWritten={endDateWritten !== startDateWritten ? endDateWritten : undefined}
        timeWritten={endTimeWritten}
        sideInfo={flightSegment.arrival.airport.iata || flightSegment.arrival.airport.icao}
        addressLine1={<AirportPreview airportId={flightSegment.arrival.airport.airportId} trigger={<a onClick={() => { }}>{flightSegment.arrival.airport.name}</a>} />}
        addressLine2={`${flightSegment.arrival.airport.location}`}
      />

      <PreviewPanelHorizontalDivider />

      <PreviewPanelDetailRow labelTop={RESX.Flight.FlightNumber}>
        <span>{flightSegment.flightNumber}</span>
      </PreviewPanelDetailRow>

      {flightSegment.seats && <PreviewPanelDetailRow labelTop={RESX.Flight.Seats}>
        <span>{flightSegment.seats}</span>
      </PreviewPanelDetailRow>}

      {flightSegment.airline && <PreviewPanelDetailRow labelTop={RESX.Flight.Airline}>
        <span>{flightSegment.airline.name}</span>
      </PreviewPanelDetailRow>}

      {flight.bookingCode && <PreviewPanelDetailRow labelTop={RESX.Flight.BookingCode}>
        <span>{flight.bookingCode}</span>
      </PreviewPanelDetailRow>}

    </PreviewPanelContent>

    <PreviewPanelFooter>
      <ButtonPrimary href={routes.flight_Detail(flight.id).absolute} label={RESX.Buttons.DetailView} />
      {flight.permissions.canModify && <ButtonTertiary
        onClick={() => SystemOneLibrary.exports.openFlightEditModal(flightSegmentId)}
        label={RESX.Buttons.btnEdit}
      />}
    </PreviewPanelFooter>
  </>
}

export interface FlightSegmentPreviewProps {
  flightSegmentId: number
  trigger: JSX.Element
}

export const FlightSegmentPreview = (props: FlightSegmentPreviewProps) => {
  return <PreviewPanel
    trigger={props.trigger}
    size="medium"
    id={`flight-segment-preview-${props.flightSegmentId}`}
  >
    <FlightSegmentPreviewPanel flightSegmentId={props.flightSegmentId} />
  </PreviewPanel>
};